import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
} from "framer-motion";
import { ChevronDown } from "lucide-react";
import { fadeUp, OutlineButton, TextCta } from "./primitives";
import PassportCard from "@/components/provena/PassportCard";
import { useLanguage } from "@/i18n/LanguageContext";
import { buildSamplePassport } from "@/lib/demoPassport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REDUCE =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const HeroSection = ({ registerTo }) => {
  const { t } = useLanguage();
  const trust = t("hero.trust");
  const samplePassport = buildSamplePassport(t);

  // Pointer parallax (normalised -0.5..0.5), smoothed by a spring for 60fps feel.
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, { stiffness: 60, damping: 18, mass: 0.4 });
  const sy = useSpring(py, { stiffness: 60, damping: 18, mass: 0.4 });
  const { scrollY } = useScroll();

  // Depth layers — each moves by a different amount (transform-only = GPU).
  // Deliberately understated parallax — felt more than seen.
  const cardX = useTransform(sx, (v) => v * 8);
  const cardY = useTransform([sy, scrollY], ([m, s]) => m * 8 + s * 0.05);
  const glowX = useTransform(sx, (v) => v * 18);
  const glowXNeg = useTransform(sx, (v) => v * -18);
  const glowY = useTransform(sy, (v) => v * 18);
  const textY = useTransform(scrollY, [0, 500], [0, -16]);

  const onMove = (e) => {
    if (REDUCE) return;
    const r = e.currentTarget.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width - 0.5);
    py.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    px.set(0);
    py.set(0);
  };

  const par = !REDUCE; // apply parallax styles only when motion is allowed

  return (
    <section
      data-testid="hero-section"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 min-h-screen flex items-center overflow-hidden"
    >
      {/* Depth glow layers */}
      <motion.div
        aria-hidden="true"
        style={par ? { x: glowX, y: glowY } : undefined}
        className="pointer-events-none absolute top-1/3 -left-40 w-96 h-96 rounded-full bg-gold/10 blur-[120px]"
      />
      <motion.div
        aria-hidden="true"
        style={par ? { x: glowXNeg, y: glowY } : undefined}
        className="pointer-events-none absolute bottom-10 right-0 w-80 h-80 rounded-full bg-gold/[0.06] blur-[130px]"
      />

      <div className="max-w-7xl mx-auto px-5 md:px-12 w-full grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          style={par ? { y: textY } : undefined}
          className="relative z-10"
        >
          <motion.div
            variants={fadeUp}
            custom={0}
            className="mb-8 flex flex-wrap items-center gap-x-4 gap-y-2"
          >
            <span className="font-serif text-gold-gradient engraved text-lg leading-none tracking-[0.15em]">
              Nº&nbsp;0001
            </span>
            <span
              aria-hidden="true"
              className="h-px w-10 bg-gradient-to-r from-gold/50 to-transparent"
            />
            <span
              data-testid="hero-eyebrow"
              className="text-xs md:text-sm tracking-[0.35em] uppercase text-gold-ink"
            >
              {t("hero.eyebrow")}
            </span>
          </motion.div>
          {/* Two sentences: the statement, then the promise. The gold is still
              here; it no longer shimmers. */}
          <motion.h1
            variants={fadeUp}
            custom={1}
            className="font-serif text-[2.1rem] sm:text-4xl lg:text-5xl xl:text-[3.4rem] leading-[1.18] tracking-tight mb-8 max-w-2xl"
          >
            {t("hero.titlePre")}
            <span className="mt-2 block italic text-gold-ink">
              {t("hero.titleHighlight")}
            </span>
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={2}
            className="text-base md:text-lg text-ink-muted leading-loose max-w-xl mb-12"
          >
            {t("hero.subtitle")}
          </motion.p>
          <motion.div
            variants={fadeUp}
            custom={3}
            className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10"
          >
            <OutlineButton to={registerTo} testId="hero-register-button" track="hero">
              {t("hero.register")}
            </OutlineButton>
            <TextCta to="/verifier" testId="hero-verify-button">
              {t("hero.verify")}
            </TextCta>
          </motion.div>

          {Array.isArray(trust) && (
            <motion.ul
              variants={fadeUp}
              custom={4}
              data-testid="hero-trust"
              className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
            >
              {/* A thin gold rule, not a shield: the claim stands on its own. */}
              {trust.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-3 text-xs md:text-sm text-ink-muted tracking-wide"
                >
                  <span aria-hidden="true" className="h-px w-5 shrink-0 bg-gold/60" />
                  {item}
                </li>
              ))}
            </motion.ul>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          style={par ? { x: cardX, y: cardY } : undefined}
          className="relative"
        >
          {/* RDEC-020 : le héros montre ce que Provena remet, un passeport, et
              non le logo déjà présent dans l'en-tête juste au-dessus. */}
          <div data-testid="hero-passport" className="relative max-w-md mx-auto lg:ml-auto animate-float">
            <div className="pointer-events-none absolute inset-6 rounded-full bg-gold/[0.08] blur-[80px] animate-pulse-gold" />
            <PassportCard
              data={samplePassport}
              t={t}
              qrSrc={`${API}/verify/${samplePassport.provena_id}/qr`}
            />
            <p className="mt-4 text-center text-[11px] leading-relaxed text-ink-dim">
              {t("pricing.passportPrivacy")}
            </p>
          </div>
        </motion.div>
      </div>

      <a
        href="#probleme"
        aria-label={t("hero.scroll")}
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-ink-subtle hover:text-gold-ink transition-colors"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">
          {t("hero.scroll")}
        </span>
        <ChevronDown size={18} className="animate-bounce" />
      </a>
    </section>
  );
};

export default HeroSection;
